<script setup lang="ts">
import type { RiskZone, RotorWingZoneExposure } from "@ca/types/rotor_wing";

import { computed, ref } from "vue";

const props = defineProps<{
	zones: RiskZone[];
	exposures?: RotorWingZoneExposure[];
	/** Truncate reasoning text */
	truncate?: boolean;
	/** Mini mode - show circular buttons with just color, icon, and number */
	mini?: boolean;
}>();

const exposureText = (zone: RiskZone) => {
	const exposure = props.exposures?.find(item => item.zoneIndex === props.zones.indexOf(zone));
	return exposure
		? `Closest: ${exposure.closestDistanceNm.toFixed(1)} NM · ${(exposure.durationSeconds / 60).toFixed(1)} min inside (${(exposure.flightProportion * 100).toFixed(1)}% of flight)`
		: null;
};

const emit = defineEmits<{
	select: [zone: RiskZone];
}>();

const selectedZone = ref<RiskZone | null>(null);

const isZoneDetailOpen = computed({
	get: () => selectedZone.value !== null,
	set: (value: boolean) => {
		if (!value) {
			selectedZone.value = null;
		}
	},
});

const getRiskLabel = (risk: number): string => {
	if (risk <= 27) {
		return "Low";
	}
	if (risk <= 42) {
		return "Moderate";
	}
	if (risk <= 60) {
		return "Elevated";
	}
	if (risk <= 80) {
		return "High";
	}
	return "Severe";
};

const getRiskBgClass = (risk: number): string => {
	if (risk <= 27) {
		return "bg-green-500";
	}
	if (risk <= 42) {
		return "bg-yellow-500";
	}
	if (risk <= 60) {
		return "bg-orange-500";
	}
	if (risk <= 80) {
		return "bg-red-500";
	}
	return "bg-red-800";
};

const getRiskTextClass = (risk: number): string => {
	if (risk <= 27) {
		return "text-green-500";
	}
	if (risk <= 42) {
		return "text-yellow-500";
	}
	if (risk <= 60) {
		return "text-orange-500";
	}
	if (risk <= 80) {
		return "text-red-500";
	}
	return "text-red-800";
};

const getCategoryIcon = (category: string): string => {
	switch (category) {
		case "notam": return "mdi-clipboard-text-outline";
		case "terrain": return "mdi-terrain";
		case "weather": return "mdi-weather-partly-cloudy";
		case "wind": return "mdi-weather-windy";
		case "icing": return "mdi-snowflake";
		case "thunderstorm": return "mdi-weather-lightning";
		case "visibility": return "mdi-weather-fog";
		case "airspace": return "mdi-airplane";
		case "density-altitude": return "mdi-arrow-up-bold";
		default: return "mdi-alert";
	}
};

const handleClick = (zone: RiskZone) => {
	selectedZone.value = zone;
	emit("select", zone);
};
</script>

<template>
  <!-- Mini mode: circular buttons -->
  <div
    v-if="mini"
    class="flex flex-wrap gap-2"
  >
    <button
      v-for="zone in zones"
      :key="zone.id"
      :class="[
        'flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border transition-colors hover:opacity-80 cursor-pointer',
        getRiskBgClass(zone.risk),
        'border-transparent'
      ]"
      @click="handleClick(zone)"
    >
      <UIcon
        :name="getCategoryIcon(zone.category)"
        class="size-3.5 text-white"
      />
      <span class="font-cta text-xs text-white">{{ zone.risk }}</span>
    </button>
  </div>

  <!-- Full mode: list with details -->
  <div
    v-else
    class="space-y-2"
  >
    <button
      v-for="zone in zones"
      :key="zone.id"
      class="w-full text-left p-3 rounded-lg border border-gray-300 transition-colors hover:bg-elevated cursor-pointer"
      @click="handleClick(zone)"
    >
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <UIcon
            :name="getCategoryIcon(zone.category)"
            class="size-4 text-gray-400"
          />
          <span class="font-cta text-sm capitalize">{{ zone.category }}</span>
        </div>
        <div class="flex items-center gap-2">
          <div
            :class="getRiskBgClass(zone.risk)"
            class="size-2 rounded-full"
          />
          <span
            :class="getRiskTextClass(zone.risk)"
            class="font-cta text-sm"
          >{{ zone.risk }}</span>
        </div>
      </div>
      <p
        :class="[
          'text-xs text-gray-500 mt-1',
          truncate && 'truncate'
        ]"
      >
        {{ zone.reasoning }}
      </p>
      <p
        v-if="exposureText(zone)"
        class="text-xs text-gray-500 mt-2"
      >
        {{ exposureText(zone) }}
      </p>
    </button>
  </div>

  <!-- Zone Detail Modal -->
  <UModal v-model:open="isZoneDetailOpen">
    <template #header>
      <div
        v-if="selectedZone"
        class="flex items-center gap-3"
      >
        <UIcon
          :name="getCategoryIcon(selectedZone.category)"
          class="size-8 text-brand-500"
        />
        <div>
          <h3 class="font-cta text-lg capitalize">
            {{ selectedZone.category }} Risk
          </h3>
          <div class="flex items-center gap-2 mt-1">
            <div
              :class="getRiskBgClass(selectedZone.risk)"
              class="size-2 rounded-full"
            />
            <span
              :class="getRiskTextClass(selectedZone.risk)"
              class="text-sm font-cta"
            >
              {{ selectedZone.risk }} - {{ getRiskLabel(selectedZone.risk) }}
            </span>
          </div>
        </div>
      </div>
    </template>
    <template #body>
      <div
        v-if="selectedZone"
        class="space-y-4"
      >
        <div>
          <p class="text-xs text-gray-500 uppercase tracking-wide mb-1">
            Assessment
          </p>
          <p>
            {{ selectedZone.reasoning }}
          </p>
        </div>

        <div>
          <p class="text-xs text-gray-500 uppercase tracking-wide mb-1">
            Affected Area
          </p>
          <p>
            {{ selectedZone.radiusNm }} NM radius from
            {{ (selectedZone.center[1] ?? 0).toFixed(4) }}°N, {{ Math.abs(selectedZone.center[0] ?? 0).toFixed(4) }}°W
          </p>
        </div>

        <div v-if="exposureText(selectedZone)">
          <p class="text-xs text-gray-500 uppercase tracking-wide mb-1">
            Actual Flight Exposure
          </p>
          <p>{{ exposureText(selectedZone) }}</p>
        </div>

        <div>
          <p class="text-xs text-gray-500 uppercase tracking-wide mb-2">
            Sources
          </p>
          <div class="flex flex-wrap gap-2">
            <span
              v-for="source in selectedZone.sources"
              :key="source"
              class="text-xs bg-gray-200 text-gray-800 px-2 py-1 rounded"
            >
              {{ source }}
            </span>
          </div>
        </div>
      </div>
    </template>
  </UModal>
</template>
